import { useCallback, useEffect, useState } from 'react';

/**
 * Кеш живёт в модуле, а не в состоянии компонента: экраны размонтируются при
 * каждом переходе по табам, и без внешнего хранилища возврат на уже виденный
 * экран каждый раз заново показывал бы лоадер и заново дёргал бы сеть.
 */
const cache = new Map<string, unknown>();

/** Запросы «в полёте»: два компонента с одним ключом не должны слать два запроса. */
const inFlight = new Map<string, Promise<unknown>>();

/**
 * Префикс ключей того, что меняется от занятий: статистика, активность по
 * дням, статусы сценариев практики. Такие данные не перезапрашиваются при
 * каждом открытии вкладки — их сбрасывают одним `invalidateCache(PROGRESS_CACHE_PREFIX)`
 * там, где человек реально позанимался: выход из чата, выход из сессии
 * карточек (см. `ChatScreen`, `LearnPage`, `ReviewPage`).
 */
export const PROGRESS_CACHE_PREFIX = 'progress:';

/** Префиксы, чьи запросы ждут промис, прежде чем уйти в сеть, — см. `holdCacheUntil`. */
const holds = new Map<string, Promise<unknown>>();

/**
 * Сбрасывает ключи с префиксом и придерживает их новые запросы, пока не
 * завершится `until`. Для случая «сервер ещё досчитывает»: выход из чата —
 * главная монтируется сразу, а `/end` ещё летит. Раньше сбрасывали дважды
 * (при выходе и после `/end`), и главная запрашивала стрик, ролевые и идиомы
 * два раза подряд — первый ответ мог прийти без только что законченного
 * визита. Теперь запрос один, и уходит он уже после `/end`.
 */
export function holdCacheUntil(keyPrefix: string, until: Promise<unknown>): void {
  const settled = until.then(
    () => undefined,
    () => undefined,
  );
  holds.set(keyPrefix, settled);
  void settled.then(() => {
    if (holds.get(keyPrefix) === settled) holds.delete(keyPrefix);
  });
  invalidateCache(keyPrefix);
}

function heldBy(key: string): Promise<unknown> | undefined {
  for (const [prefix, hold] of holds) if (key.startsWith(prefix)) return hold;
  return undefined;
}

function request<T>(key: string, load: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const hold = heldBy(key);
  const started = (hold ? hold.then(load) : load()).finally(() => {
    // После `invalidateCache` здесь может лежать уже новый запрос — его не трогаем.
    if (inFlight.get(key) === started) inFlight.delete(key);
  });
  inFlight.set(key, started);
  return started;
}

/**
 * То же, что `useCachedQuery`, но вне рендера — для кода, которому нужен сам
 * промис (провайдер контекста, загрузка до навигации). Кеш и запрос «в полёте»
 * общие с хуком: оба вместе дают один сетевой запрос на ключ.
 */
export function fetchCached<T>(key: string, load: () => Promise<T>): Promise<T> {
  if (cache.has(key)) return Promise.resolve(cache.get(key) as T);
  const startedEpoch = epoch.get(key) ?? 0;
  return request(key, load).then((data) => {
    // Пока запрос летел, ключ сбросили — ответ мог уйти до изменения на
    // сервере (выход из чата: `/end` ещё не долетел). В кеш его не кладём,
    // иначе устаревшие цифры жили бы до следующего сброса.
    if ((epoch.get(key) ?? 0) === startedEpoch) cache.set(key, data);
    return data;
  });
}

/**
 * Счётчик перезапросов на ключ. `cache.delete` сам по себе не будит хук,
 * висящий на этом ключе: его эффект зависит только от `key`, а тот после
 * инвалидации не меняется — без отдельного сигнала isLoading застревал бы в
 * true навсегда (см. возврат на экран статистики после удаления ошибки).
 * Эпоха — то, что реально меняется и попадает в зависимости эффекта.
 */
const epoch = new Map<string, number>();

/**
 * Сбрасывает закешированное — после действия, которое меняет данные на
 * сервере (например, удаления ошибки: счётчики тем и график устарели).
 * Без аргумента чистит всё, с префиксом — только совпавшие ключи.
 */
export function invalidateCache(keyPrefix?: string): void {
  // И то, что ещё летит: его ответ мог уйти до изменения на сервере — новый
  // запрос пойдёт заново, а не присоединится к старому (см. `fetchCached`).
  const keys = new Set([...cache.keys(), ...inFlight.keys()]);
  for (const key of keys) {
    if (keyPrefix !== undefined && !key.startsWith(keyPrefix)) continue;
    cache.delete(key);
    inFlight.delete(key);
    epoch.set(key, (epoch.get(key) ?? 0) + 1);
  }
}

/**
 * Правит уже закешированные данные на месте, без похода в сеть — в отличие
 * от `invalidateCache` не сбрасывает эпоху, так что уже смонтированный хук на
 * этом ключе не запускает повторный запрос: следующий его рендер (по любой
 * другой причине — своё изменение состояния и т.п.) просто читает
 * поправленное значение из кеша. Годится, когда результат действия уже
 * известен на клиенте и без сервера (например, элемент удалён — не ждать же
 * скелетон ради вычитания одной строки из уже имеющегося списка).
 */
export function updateCache<T>(key: string, updater: (current: T) => T): void {
  if (!cache.has(key)) return;
  cache.set(key, updater(cache.get(key) as T));
  for (const listener of listeners.get(key) ?? []) listener();
}

/**
 * Кто сейчас показывает ключ — `updateCache` перерисовывает их всех. Без
 * этого правка на одном экране (имя в настройках) не доходила до соседнего
 * компонента с тем же ключом (имя в сайдбаре) до его следующей перерисовки.
 */
const listeners = new Map<string, Set<() => void>>();

export interface CachedQueryResult<T> {
  data: T | null;
  error: unknown;
  /** Данных ещё нет — рисуем скелет. Из кеша данные приходят сразу, минуя это состояние. */
  isLoading: boolean;
  reload: () => void;
}

/**
 * Загрузка с кешом по ключу. Уже загруженное отдаётся синхронно первым же
 * рендером — переключение периода назад к виденному или возврат на экран
 * происходят без мигания скелетом.
 *
 * `load` намеренно не в зависимостях: инлайновая стрелка на месте вызова
 * меняется каждый рендер и зациклила бы эффект. Ключ — единственный признак
 * того, что нужны другие данные.
 */
/**
 * Сколько минимум держать скелет после «Обновить», если запрос снова упал.
 * Без сети `fetch` отказывает мгновенно — заглушка ошибки возвращалась в том
 * же кадре, и казалось, что кнопка ничего не делает.
 */
const RETRY_MIN_VISIBLE_MS = 600;

/**
 * Когда нажали «Обновить», по ключу — а не в экземпляре хука: на главной один
 * ключ слушают и виджет, и сама страница (решает, показать ли общий экран), и
 * задержка нужна обоим одинаковая, иначе карточки блоков мелькали бы раньше.
 */
const retryStartedAt = new Map<string, number>();

export function useCachedQuery<T>(key: string, load: () => Promise<T>): CachedQueryResult<T> {
  const [, forceRender] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const cached = cache.get(key) as T | undefined;
  const currentEpoch = epoch.get(key) ?? 0;

  useEffect(() => {
    const listener = () => forceRender((value) => value + 1);
    let set = listeners.get(key);
    if (!set) listeners.set(key, (set = new Set()));
    set.add(listener);
    return () => {
      set.delete(listener);
    };
  }, [key]);

  useEffect(() => {
    if (cache.has(key)) return;

    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setError(null);

    // Общий промис на ключ: у StrictMode двойной эффект и у двух компонентов
    // с одним ключом получится один сетевой запрос, а не два.
    fetchCached(key, load)
      .then(() => {
        if (active) forceRender((value) => value + 1);
      })
      .catch((err: unknown) => {
        if (!active) return;
        const startedAt = retryStartedAt.get(key);
        const wait = startedAt === undefined ? 0 : RETRY_MIN_VISIBLE_MS - (Date.now() - startedAt);
        if (wait <= 0) setError(err);
        else timer = setTimeout(() => active && setError(err), wait);
      });

    return () => {
      active = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, currentEpoch]);

  const reload = useCallback(() => {
    retryStartedAt.set(key, Date.now());
    cache.delete(key);
    epoch.set(key, (epoch.get(key) ?? 0) + 1);
    setError(null);
    forceRender((value) => value + 1);
  }, [key]);

  return {
    data: cached ?? null,
    error,
    isLoading: cached === undefined && error == null,
    reload,
  };
}
